import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchEmployeeDirectoryData } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryData/fetchEmployeeDirectoryDataApi";
import { EmployeeDirectorySpecialty } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryData/types";
import { Employee } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryProfiles/types";
import { DirectoryTeam } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryTeams/types";

export const ALL_TEAMS_FILTER = "all";

export interface UseEmployeeDirectoryPageReturn {
  /** Employee uuid of the viewer company's assigned coach (#9651). */
  assignedCoachEmployeeUuid: string | null;
  loading: boolean;
  members: Employee[];
  filteredMembers: Employee[];

  teams: DirectoryTeam[];
  activeTeamUuid: string;
  setActiveTeamUuid: (uuid: string) => void;

  specialtyOptions: EmployeeDirectorySpecialty[];
  selectedSpecialtyUuids: string[];
  toggleSpecialty: (uuid: string) => void;
  clearSpecialties: () => void;
}

export const useEmployeeDirectoryPage = (): UseEmployeeDirectoryPageReturn => {
  const [members, setMembers] = useState<Employee[]>([]);
  const [teams, setTeams] = useState<DirectoryTeam[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<
    EmployeeDirectorySpecialty[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTeamUuid, setActiveTeamUuid] =
    useState<string>(ALL_TEAMS_FILTER);
  const [selectedSpecialtyUuids, setSelectedSpecialtyUuids] = useState<
    string[]
  >([]);
  const [assignedCoachEmployeeUuid, setAssignedCoachEmployeeUuid] = useState<
    string | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchEmployeeDirectoryData()
      .then((response) => {
        if (cancelled) return;
        setMembers(response.data.profiles);
        setTeams(response.data.teams);
        setSpecialtyOptions(response.data.specialties);
        setAssignedCoachEmployeeUuid(
          response.data.assignedCoachEmployeeUuid ?? null,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleSpecialty = useCallback((uuid: string) => {
    setSelectedSpecialtyUuids((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid],
    );
  }, []);

  const clearSpecialties = useCallback(() => {
    setSelectedSpecialtyUuids([]);
  }, []);

  const filteredMembers = useMemo(() => {
    const selectedSpecialtySet = new Set(selectedSpecialtyUuids);
    const isAssignedCoach = (member: (typeof members)[number]) =>
      assignedCoachEmployeeUuid !== null &&
      member.employee.uuid === assignedCoachEmployeeUuid;
    const visible = members.filter((member) => {
      if (activeTeamUuid !== ALL_TEAMS_FILTER) {
        const inTeam = member.teams.some((t) => t.uuid === activeTeamUuid);
        if (!inTeam) return false;
      }
      if (selectedSpecialtySet.size > 0) {
        const hasAny = member.specialties.some((s) =>
          selectedSpecialtySet.has(s.uuid),
        );
        if (!hasAny) return false;
      }
      return true;
    });
    // The member's own coach leads the grid (#9651), unaffected by the
    // alphabetical ordering of everyone else.
    return [
      ...visible.filter(isAssignedCoach),
      ...visible.filter((member) => !isAssignedCoach(member)),
    ];
  }, [
    members,
    activeTeamUuid,
    selectedSpecialtyUuids,
    assignedCoachEmployeeUuid,
  ]);

  return {
    assignedCoachEmployeeUuid,
    loading,
    members,
    filteredMembers,

    teams,
    activeTeamUuid,
    setActiveTeamUuid,

    specialtyOptions,
    selectedSpecialtyUuids,
    toggleSpecialty,
    clearSpecialties,
  };
};
